import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DndContext, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { get } from 'lodash';
import classNames from 'classnames';
import Pficon from 'components/Pficon';
import { handleMove } from 'containers/QuestionnairePage/utils';
import { wouldDragCrossScreeningBoundary } from 'containers/QuestionnairePage/screeningBoundaryUtils';
import { withRouter } from 'react-router-dom';
import { isLoopAndMergeGroup } from 'utils/nodeTypes';
import {
  DRAG_TYPE_QUESTION,
  DRAG_TYPE_GROUP,
  DRAG_TYPE_GROUP_PROPERTY,
  DRAG_TYPE_ATTRIBUTE_LEVEL,
  DRAG_TYPE_QUESTION_IN_GROUP,
  DRAG_TYPE_SCREENING_QUESTION,
} from './constants';

// This is useful for reusable components that do not always have a draggable parent
function PrivateWithOptionalDrag(props) {
  const { dragDropManager } = useContext(DndContext);
  if (!dragDropManager) {
    return props.children;
  }
  return <WithDrag {...props} />;
}
function PrivateWithDrag(props) {
  const [{ isDragging }, drag, preview] = useDrag({
    item: props.item,
    type: props.item.type,
    canDrag: () => !props.disabled,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const dropRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const isInsideGroup = !!props.item.groupRefId;
  let acceptedTypes = [DRAG_TYPE_QUESTION, DRAG_TYPE_GROUP];
  if (isInsideGroup) {
    if (!props.item.acceptDrop) {
      acceptedTypes = [];
    } else {
      acceptedTypes = [DRAG_TYPE_QUESTION];
    }
  }

  if (props.item.type === DRAG_TYPE_GROUP_PROPERTY) {
    acceptedTypes = [DRAG_TYPE_GROUP_PROPERTY];
  }

  if (props.item.type === DRAG_TYPE_ATTRIBUTE_LEVEL) {
    acceptedTypes = [DRAG_TYPE_ATTRIBUTE_LEVEL];
  }

  if (props.item.type === DRAG_TYPE_QUESTION_IN_GROUP) {
    acceptedTypes = [DRAG_TYPE_QUESTION_IN_GROUP];
  }

  if (props.item.type === DRAG_TYPE_SCREENING_QUESTION) {
    acceptedTypes = [DRAG_TYPE_SCREENING_QUESTION];
  }

  if (!props.item.acceptDrag) {
    acceptedTypes = [];
  }

  const [, drop] = useDrop({
    accept: acceptedTypes,

    drop: (droppedItem, monitor) => {
      if (!monitor.isOver({ shallow: true })) {
        return;
      }

      handleMove({
        source: droppedItem,
        target: props.item,
        onMove: props.onDrop,
        offset: isHovered === 'top' ? 0 : 1,
      });

      setIsHovered(false);
    },
    hover: (itemToDrop, monitor) => {
      const canDropRightNow = monitor.canDrop();
      const isOverRightNow = monitor.isOver({ shallow: true });

      if (!dropRef.current || !canDropRightNow || !isOverRightNow) {
        if (isHovered) setIsHovered(false);
        return;
      }
      const dropTargetRect = dropRef.current.getBoundingClientRect();
      const dropTargetMiddle = dropTargetRect.y + dropTargetRect.height / 2;
      const mousePosition = monitor.getClientOffset();

      if (mousePosition) {
        const half = mousePosition.y > dropTargetMiddle ? 'bottom' : 'top';
        if (isHovered !== half) setIsHovered(half);
        return;
      }

      if (isHovered) {
        setIsHovered(false);
      }
    },
    canDrop: (itemToDrop) => {
      const inSameList = props.item.type === itemToDrop.type;

      if (inSameList && props.item.id === itemToDrop.id) {
        return false;
      }

      // handle Van-Westendorp NMS questionsonly allowed to be dropped
      // inside their own VW group (cannot be dragged to other VW group)
      if (
        inSameList &&
        itemToDrop.type === DRAG_TYPE_QUESTION_IN_GROUP &&
        itemToDrop.groupRefId !== props.item.groupRefId
      ) {
        return false;
      }

      const { enforceScreeningBoundary = false, boundaryQuestions = [] } =
        itemToDrop;

      if (
        inSameList &&
        enforceScreeningBoundary &&
        boundaryQuestions.length > 0 &&
        !itemToDrop.groupRefId &&
        !props.item.groupRefId
      ) {
        const wouldCrossForOffset = (offset) =>
          wouldDragCrossScreeningBoundary({
            questions: boundaryQuestions,
            sourceRefId: itemToDrop.id,
            sourceIndex: itemToDrop.index,
            targetIndex: props.item.index,
            offset,
          });

        if (wouldCrossForOffset(0) || wouldCrossForOffset(1)) {
          return false;
        }
      }

      // attribute levels can only be reordered within the same attribute
      if (
        inSameList &&
        itemToDrop.type === DRAG_TYPE_ATTRIBUTE_LEVEL &&
        itemToDrop.attributeRefId !== props.item.attributeRefId
      ) {
        return false;
      }

      return true;
    },
    collect: (monitor) => {
      const isOver = monitor.isOver({ shallow: true });

      if (!isOver && isHovered) {
        setIsHovered(false);
      }
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const location = get(props, 'location.pathname', '');
  const isInThemePage = location.includes('theme');

  return (
    <div
      ref={(node) => {
        dropRef.current = node;
        drop(node);
      }}
      className={classNames(
        'position-relative',
        'drag-card',
        `drag-type-${props.item.type}`,
        'border-top-transparent',
        'border-bottom-transparent',
        { 'opacity-40': isDragging },
        {
          'drag-guide-bottom': isHovered === 'bottom',
          'drag-guide-top': isHovered === 'top',
          'group-active': props.isActive || props.isSubQuestionActive,
          'reset-grippy': isInThemePage,
          'group-same-page': props.hasGroupSamePageSetting,
          'group-loop-and-merge': isLoopAndMergeGroup(props.item.nodeType),
        },
      )}
    >
      <div
        ref={(node) => {
          dragRef.current = node;
          drag(node);
        }}
        className={classNames('grippy', {
          'color-grey-7': props.disabled,
          'cursor-disabled': props.disabled,
        })}
        // This needs to be more than questionnaire-card-hidden CSS class z-index
        style={{ zIndex: 4 }}
      >
        <div>
          <Pficon type="drag" />
        </div>
      </div>
      {props.children}
    </div>
  );
}

PrivateWithDrag.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  item: PropTypes.object,
  onDrop: PropTypes.func,
  isActive: PropTypes.bool,
  isSubQuestionActive: PropTypes.bool,
  disabled: PropTypes.bool,
};

const WithDrag = withRouter(PrivateWithDrag);
const WithOptionalDrag = withRouter(PrivateWithOptionalDrag);
export { WithOptionalDrag, WithDrag };
